import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import CommissionBasket from '../components/CommissionBasket';
import Gated from '../components/Gated';
import { useEntitlements } from '../contexts/EntitlementsContext';
import { track } from '../lib/paywall';
import { AlertTriangle, ArrowUpDown, ChevronLeft, ChevronRight, Download, Filter, MapPin, Target, TrendingDown, Plus, Lock } from 'lucide-react';
import { toast } from 'sonner';

const RISK_COLORS = { High: '#8A1A1A', Medium: '#8A5A14', Low: '#2D6E4E', Unknown: '#B8B4AD' };
const DISTRICTS = ['', 'TS15', 'TS16', 'TS17', 'TS18', 'TS19', 'TS20', 'TS21', 'TS22', 'TS23'];
const BASKET_KEY = 'sdr_commission_basket';

export default function InterventionMatrix() {
  const { has, variant } = useEntitlements();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [district, setDistrict] = useState('');
  const [sector, setSector] = useState('');
  const [risk, setRisk] = useState('');
  const [page, setPage] = useState(1);
  const [basket, setBasket] = useState(() => {
    try { return JSON.parse(localStorage.getItem(BASKET_KEY) || '[]'); } catch (e) { console.warn('InterventionMatrix: bad basket in storage', e); return []; }
  });

  const persistBasket = (next) => {
    setBasket(next);
    try { localStorage.setItem(BASKET_KEY, JSON.stringify(next)); } catch (e) { console.warn('InterventionMatrix: could not persist basket', e); }
  };

  const inBasket = (id) => basket.some(b => b.business_id === id);
  const toggleBasket = (b) => {
    if (inBasket(b.id)) {
      persistBasket(basket.filter(x => x.business_id !== b.id));
    } else {
      persistBasket([...basket, { business_id: b.id, business_name: b.company_name, commission_type: 'audit' }]);
      toast.success(`Added "${b.company_name}" to basket`, { duration: 1500 });
    }
  };
  const addAllVisible = () => {
    const visible = (data?.businesses || []).filter(b => !inBasket(b.id))
      .map(b => ({ business_id: b.id, business_name: b.company_name, commission_type: 'audit' }));
    if (!visible.length) { toast.info('All visible businesses already in basket.'); return; }
    persistBasket([...basket, ...visible]);
    toast.success(`Added ${visible.length} business${visible.length !== 1 ? 'es' : ''}`);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (district) params.set('district', district);
      if (sector) params.set('sector', sector);
      if (risk) params.set('risk', risk);
      const { data: d } = await api.get(`/interventions/priority-matrix?${params}`);
      setData(d);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [district, sector, risk, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const exportCSV = () => {
    if (!has('csv_export')) {
      toast.error('CSV export requires Premium. Upgrade to download.', {
        action: { label: 'Upgrade', onClick: () => window.location.href = '/pricing' },
      });
      return;
    }
    if (!data?.businesses?.length) return;
    const headers = ['Priority Score', 'Company Name', 'CH Number', 'Postcode', 'District', 'Sector', 'Digital Score', 'IMD Decile', 'Risk', 'Intervention'];
    const rows = data.businesses.map(b => [
      b.priority_score, `"${b.company_name}"`, b.ch_number, b.postcode,
      b.postcode_district, `"${b.sector}"`, b.digital_score, b.imd_decile,
      b.connectivity_risk, `"${b.priority_intervention || ''}"`,
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `intervention_priority_${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  if (loading && !data) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 rounded bg-brand-orange animate-pulse" />
    </div>
  );

  const s = data?.summary || {};

  return (
    <div className="max-w-[1200px] mx-auto px-4 lg:px-6 py-6 space-y-6" data-testid="intervention-matrix">
      {/* Header */}
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-ink" data-testid="matrix-title">Intervention Priority Matrix</h1>
        <p className="font-body text-sm text-ink/50 max-w-3xl leading-relaxed mt-1">
          Ranks {s.total_trading_invisible?.toLocaleString() || '4,749'} trading invisible businesses by deprivation + connectivity risk. Higher priority score = greater economic impact per intervention pound.
        </p>
      </div>

      {/* Locked-preview banner */}
      {data?.locked && (
        <div className="rounded-[6px] border border-brand-orange/30 bg-brand-orange/[0.05] p-4 flex items-start gap-3" data-testid="matrix-preview-banner" ref={(el) => { if (el && !el.dataset.viewed) { el.dataset.viewed = '1'; track('paywall_view', { source: 'matrix_banner', feature: 'priority_matrix' }); } }}>
          <div className="w-9 h-9 rounded-[4px] bg-brand-orange/15 flex items-center justify-center shrink-0">
            <Lock className="w-4 h-4 text-brand-orange" strokeWidth={2} />
          </div>
          <div className="flex-1">
            <p className="font-display text-sm font-extrabold text-ink">{variant.banner_copy}</p>
            <p className="font-body text-xs text-ink/65 leading-relaxed mt-0.5">
              Showing the top {data.businesses?.length || 3} priority businesses + summary stats. Pro plan unlocks the full ranked list of {data.total?.toLocaleString()} businesses, advanced filters, CSV export, and bulk audit commissioning.
            </p>
          </div>
          <Button asChild size="sm" className="bg-brand-orange hover:bg-brand-orange/90 text-white font-body text-xs h-8" data-testid="matrix-banner-cta" onClick={() => track('upgrade_cta_click', { source: 'matrix_banner', feature: 'priority_matrix' })}>
            <a href="/pricing">Upgrade →</a>
          </Button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="summary-cards">
        <div className="bg-white border border-ink/8 p-4 rounded">
          <div className="font-display text-3xl font-extrabold text-[#8A1A1A]">{s.total_trading_invisible?.toLocaleString()}</div>
          <div className="font-body text-[10px] uppercase tracking-wider text-ink/40">Trading Invisible</div>
        </div>
        <div className="bg-white border border-ink/8 p-4 rounded">
          <div className="font-display text-3xl font-extrabold">{s.avg_imd_decile}</div>
          <div className="font-body text-[10px] uppercase tracking-wider text-ink/40">Avg IMD Decile</div>
        </div>
        <div className="bg-white border border-ink/8 p-4 rounded">
          <div className="font-display text-3xl font-extrabold text-[#8A1A1A]">{s.high_risk?.toLocaleString()}</div>
          <div className="font-body text-[10px] uppercase tracking-wider text-ink/40">High Risk</div>
        </div>
        <div className="bg-white border border-ink/8 p-4 rounded">
          <div className="font-display text-3xl font-extrabold text-[#8A5A14]">{s.medium_risk?.toLocaleString()}</div>
          <div className="font-body text-[10px] uppercase tracking-wider text-ink/40">Medium Risk</div>
        </div>
      </div>

      {/* Charts Row */}
      {data?.by_district && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* District breakdown */}
          <div className="bg-white border border-ink/8 p-5 rounded" data-testid="district-chart">
            <h3 className="font-body text-[10px] uppercase tracking-wider text-ink/40 mb-3">By District (sorted by deprivation)</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.by_district} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="district" tick={{ fontFamily: "'IBM Plex Mono'", fontSize: 10 }} />
                <YAxis tick={{ fontFamily: "'IBM Plex Mono'", fontSize: 10 }} />
                <Tooltip contentStyle={{ fontFamily: "'IBM Plex Mono'", fontSize: 11, borderRadius: 2 }}
                  formatter={(v, name) => [v, name === 'count' ? 'Businesses' : name]} />
                <Bar dataKey="count" name="Businesses" radius={[2, 2, 0, 0]}>
                  {data.by_district.map((d, i) => (
                    <Cell key={i} fill={d.avg_imd <= 3 ? '#8A1A1A' : d.avg_imd <= 5 ? '#8A5A14' : '#2D6E4E'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="flex gap-4 mt-2 justify-center">
              <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-[#8A1A1A]" /><span className="font-body text-[9px] text-ink/40">IMD 1-3 (deprived)</span></div>
              <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-[#8A5A14]" /><span className="font-body text-[9px] text-ink/40">IMD 4-5</span></div>
              <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-[#2D6E4E]" /><span className="font-body text-[9px] text-ink/40">IMD 6+ (affluent)</span></div>
            </div>
          </div>

          {/* Sector breakdown */}
          <div className="bg-white border border-ink/8 p-5 rounded" data-testid="sector-chart">
            <h3 className="font-body text-[10px] uppercase tracking-wider text-ink/40 mb-3">By Sector</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.by_sector?.slice(0, 8)} layout="vertical" barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis type="number" tick={{ fontFamily: "'IBM Plex Mono'", fontSize: 10 }} />
                <YAxis dataKey="sector" type="category" width={140} tick={{ fontFamily: "'IBM Plex Mono'", fontSize: 9 }} />
                <Tooltip contentStyle={{ fontFamily: "'IBM Plex Mono'", fontSize: 11, borderRadius: 2 }} />
                <Bar dataKey="count" name="Businesses" fill="#FE4A2A" radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Filters + Export */}
      <div className="flex flex-wrap items-center gap-3 bg-white border border-ink/8 p-4 rounded" data-testid="matrix-filters">
        <Filter className="w-4 h-4 text-ink/30" />
        <Select value={district} onValueChange={(v) => { setDistrict(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-[140px] h-9 text-xs"><SelectValue placeholder="All Districts" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Districts</SelectItem>
            {DISTRICTS.filter(Boolean).map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={risk} onValueChange={(v) => { setRisk(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-[140px] h-9 text-xs"><SelectValue placeholder="All Risk Levels" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Risk</SelectItem>
            <SelectItem value="High">High</SelectItem>
            <SelectItem value="Medium">Medium</SelectItem>
            <SelectItem value="Low">Low</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-2">
          <span className="font-body text-[10px] text-ink/40">{data?.total?.toLocaleString() || 0} results</span>
          <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={addAllVisible} data-testid="add-all-visible-btn">
            <Plus className="w-3.5 h-3.5" /> Add page to basket
          </Button>
          <CommissionBasket items={basket} onUpdate={persistBasket} onClear={() => persistBasket([])} />
          <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={exportCSV} data-testid="export-csv">
            {has('csv_export') ? <Download className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5 text-brand-orange" />}
            Export CSV {!has('csv_export') && <span className="text-[9px] uppercase tracking-wider text-brand-orange/80 ml-0.5">Premium</span>}
          </Button>
        </div>
      </div>

      {/* Priority Table */}
      <div className="bg-white border border-ink/8 rounded overflow-hidden" data-testid="priority-table">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-[#090909] text-white">
                <th className="px-3 py-2.5 font-body text-[10px] uppercase tracking-wider font-medium w-10"></th>
                <th className="px-3 py-2.5 font-body text-[10px] uppercase tracking-wider font-medium">Priority</th>
                <th className="px-3 py-2.5 font-body text-[10px] uppercase tracking-wider font-medium">Company</th>
                <th className="px-3 py-2.5 font-body text-[10px] uppercase tracking-wider font-medium">District</th>
                <th className="px-3 py-2.5 font-body text-[10px] uppercase tracking-wider font-medium">Sector</th>
                <th className="px-3 py-2.5 font-body text-[10px] uppercase tracking-wider font-medium text-center">Score</th>
                <th className="px-3 py-2.5 font-body text-[10px] uppercase tracking-wider font-medium text-center">IMD</th>
                <th className="px-3 py-2.5 font-body text-[10px] uppercase tracking-wider font-medium text-center">Risk</th>
                <th className="px-3 py-2.5 font-body text-[10px] uppercase tracking-wider font-medium">Intervention</th>
              </tr>
            </thead>
            <tbody>
              {data?.businesses?.map((b, i) => (
                <tr key={i} className="border-t border-ink/5 hover:bg-ink/[0.02] transition-colors" data-testid={`priority-row-${i}`}>
                  <td className="px-3 py-2.5">
                    <Checkbox
                      checked={inBasket(b.id)}
                      onCheckedChange={() => toggleBasket(b)}
                      data-testid={`select-business-${i}`}
                      aria-label={`Add ${b.company_name} to commission basket`}
                    />
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded flex items-center justify-center text-white font-display text-xs font-bold ${
                        b.priority_score >= 70 ? 'bg-[#8A1A1A]' : b.priority_score >= 40 ? 'bg-[#8A5A14]' : 'bg-[#2D6E4E]'
                      }`}>
                        {b.priority_score}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="font-body text-xs font-medium text-ink truncate max-w-[200px]">{b.company_name}</div>
                    <div className="font-body text-[10px] text-ink/40">{b.ch_number} &middot; {b.postcode}</div>
                  </td>
                  <td className="px-3 py-2.5 font-body text-xs text-ink/70">{b.postcode_district}</td>
                  <td className="px-3 py-2.5 font-body text-[11px] text-ink/60 max-w-[120px] truncate">{b.sector}</td>
                  <td className="px-3 py-2.5 text-center">
                    <span className="font-display text-sm font-bold text-[#8A1A1A]">{b.digital_score}/5</span>
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <span className={`font-display text-sm font-bold ${b.imd_decile <= 3 ? 'text-[#8A1A1A]' : b.imd_decile <= 5 ? 'text-[#8A5A14]' : 'text-[#2D6E4E]'}`}>
                      {b.imd_decile}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium" style={{
                      backgroundColor: `${RISK_COLORS[b.connectivity_risk] || RISK_COLORS.Unknown}15`,
                      color: RISK_COLORS[b.connectivity_risk] || RISK_COLORS.Unknown,
                    }}>
                      {b.connectivity_risk || 'Unknown'}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 font-body text-[10px] text-ink/50 max-w-[180px] truncate">
                    {b.priority_intervention || 'Digital adoption programme'}
                  </td>
                </tr>
              ))}
              {data?.locked && data.businesses_hidden > 0 && (
                <tr className="border-t border-ink/5 bg-brand-orange/[0.04]" data-testid="matrix-locked-row">
                  <td colSpan={9} className="px-4 py-6">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-[4px] bg-brand-orange/15 flex items-center justify-center shrink-0">
                          <Lock className="w-4 h-4 text-brand-orange" strokeWidth={2} />
                        </div>
                        <div>
                          <p className="font-display text-sm font-extrabold text-ink">{data.businesses_hidden.toLocaleString()} more businesses ranked by priority</p>
                          <p className="font-body text-xs text-ink/55 mt-0.5">You're viewing the top {data.businesses?.length || 3} of {data.total?.toLocaleString()} trading-invisible businesses. Upgrade to Pro to see the full priority list, filter by district/sector, and commission audits in bulk.</p>
                        </div>
                      </div>
                      <Button asChild size="sm" className="bg-brand-orange hover:bg-brand-orange/90 text-white font-body text-sm" data-testid="matrix-locked-cta">
                        <a href="/pricing">Upgrade to Pro <ArrowUpDown className="hidden" /></a>
                      </Button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination — only show when unlocked */}
        {data && !data.locked && data.total_pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-ink/5" data-testid="pagination">
            <span className="font-body text-[10px] text-ink/40">
              Page {data.page} of {data.total_pages} &middot; {data.total.toLocaleString()} businesses
            </span>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="prev-page">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page >= data.total_pages}
                onClick={() => setPage(p => p + 1)} data-testid="next-page">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Methodology note */}
      <div className="bg-ink/[0.02] border border-ink/5 p-4 rounded" data-testid="methodology-note">
        <h3 className="font-body text-xs font-medium text-ink mb-2 flex items-center gap-1.5">
          <Target className="w-3.5 h-3.5 text-[#FE4A2A]" /> Priority Scoring Methodology
        </h3>
        <p className="font-body text-[11px] text-ink/50 leading-relaxed">
          Priority score = (10 - IMD decile) x 10 + connectivity risk bonus (High: +20, Medium: +10) - digital score x 5.
          Lower IMD decile (more deprived) areas receive higher scores. High connectivity risk areas receive bonus weighting.
          A business in a deprived area with poor connectivity and zero digital presence scores highest — representing the greatest potential
          economic impact per intervention pound spent.
        </p>
      </div>
    </div>
  );
}
